import { useEffect, lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Outlet } from 'react-router-dom';
import Header from './components/Header';
import FloatingChatButton from './components/FloatingChatButton';
import { usePyodidePreload } from './hooks/usePyodidePreload';
import { initImageSizeSettings } from './utils/imageHeightLimit';
import { initPaperFontSizeSettings } from './utils/paperFontSize';

// 首屏/高频核心路径保留静态 import，保证主路径无加载闪烁
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ExamPlatform from './pages/ExamPlatform';
import Quiz from './pages/quiz/Quiz';

// 教师端/后台/低频页面路由级懒加载（React.lazy + 动态 import），
// 配合 webpack splitChunks 按需下载 chunk，显著降低首屏体积
import TeacherLayout from './pages/teacher/TeacherLayout';
import AdminLayout from './pages/admin/AdminLayout';

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      <p className="text-sm text-gray-500">页面加载中...</p>
    </div>
  </div>
);

// 懒加载自愈：dev server 重启后沙箱反代/浏览器可能仍持旧 runtime.js（chunk 映射失效），
// 路由切换触发懒加载时请求已不存在的 chunk → ChunkLoadError 白屏。
// 捕获失败后自动整页强刷一次（sessionStorage 防循环：同一会话只自动刷一次，
// 若刷新后仍失败说明是真实代码问题，抛出原始错误走 Suspense/ErrorBoundary 流程）。
const RELOAD_FLAG = 'chunk_reload_attempted';
const lazyPage = (factory: () => Promise<{ default: React.ComponentType<any> }>) =>
  lazy(() =>
    factory()
      .then((mod) => {
        // 成功加载：清除防循环标记（说明当前构建可用，后续再失败可重新自愈一次）
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
        return mod;
      })
      .catch((err: any) => {
        const alreadyReloaded = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(RELOAD_FLAG) === '1';
        const isChunkError =
          err &&
          (err.name === 'ChunkLoadError' ||
            /Loading chunk [\d]+ failed|Loading CSS chunk [\d]+ failed|Importing a module script failed/i.test(String(err.message || err)));
        if (isChunkError && !alreadyReloaded) {
          console.warn('[lazyPage] chunk 加载失败，自动刷新页面以获取最新构建:', err?.message || err);
          try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* ignore */ }
          window.location.reload();
          return new Promise(() => {}); // 阻止错误继续抛出，等待刷新
        }
        console.error('[lazyPage] 页面 chunk 加载失败（已尝试自动刷新或非 chunk 错误）:', err?.message || err);
        throw err;
      })
  );

// ---- 学员侧低频页面 ----
const CategoryPage = lazyPage(() => import('./pages/CategoryPage'));
const ExamListPage = lazyPage(() => import('./pages/ExamListPage'));
const WrongQuestionsPage = lazyPage(() => import('./pages/WrongQuestionsPage'));
const FavoritesPage = lazyPage(() => import('./pages/FavoritesPage'));
const FavoritesReviewPage = lazyPage(() => import('./pages/FavoritesReviewPage'));
const WrongQuestionsReviewPage = lazyPage(() => import('./pages/WrongQuestionsReviewPage'));
const ExamRecordsPage = lazyPage(() => import('./pages/ExamRecordsPage'));
const ResetPassword = lazyPage(() => import('./pages/ResetPassword'));
const UserProfile = lazyPage(() => import('./pages/UserProfile'));
const ExamResult = lazyPage(() => import('./pages/ExamResult'));
const PaymentResult = lazyPage(() => import('./pages/PaymentResult'));
const HelpCenter = lazyPage(() => import('./pages/HelpCenter'));
const PrivacyPolicy = lazyPage(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyPage(() => import('./pages/TermsOfService'));
const Notice = lazyPage(() => import('./pages/Notice'));
const PayRedirect = lazyPage(() => import('./pages/PayRedirect'));
const NoticeDetail = lazyPage(() => import('./pages/NoticeDetail'));
const DownloadCenter = lazyPage(() => import('./pages/DownloadCenter'));
const PythonPlayground = lazyPage(() => import('./pages/PythonPlayground'));
const CppPlayground = lazyPage(() => import('./pages/CppPlayground'));
const ScratchEditorPage = lazyPage(() => import('./pages/ScratchEditorPage'));
const AssignmentsPage = lazyPage(() => import('./pages/AssignmentsPage'));
const PointCenter = lazyPage(() => import('./pages/PointCenter'));
const PointTransactions = lazyPage(() => import('./pages/PointTransactions'));
const MyCorrections = lazyPage(() => import('./pages/MyCorrections'));
const Message = lazyPage(() => import('./pages/Message'));
const OrderDetail = lazyPage(() => import('./pages/OrderDetail'));

// ---- 后台管理 ----
const AdminPapers = lazyPage(() => import('./pages/admin/Papers'));
const AdminMembers = lazyPage(() => import('./pages/admin/Members'));
const OperationLogs = lazyPage(() => import('./pages/admin/OperationLogs'));
const AdminUsers = lazyPage(() => import('./pages/admin/AdminUsers'));
const AdminOrders = lazyPage(() => import('./pages/admin/StudentOrders'));
const CustomerService = lazyPage(() => import('./pages/admin/CustomerService'));
const AdminMessageConfig = lazyPage(() => import('./pages/admin/AdminMessageConfig'));
const MessageTemplates = lazyPage(() => import('./pages/admin/MessageTemplates'));
const SendMessage = lazyPage(() => import('./pages/admin/SendMessage'));
const MessageStatistics = lazyPage(() => import('./pages/admin/MessageStatistics'));
const AlipayConfig = lazyPage(() => import('./pages/admin/AlipayConfig'));
const SMSSettings = lazyPage(() => import('./pages/admin/SMSSettings'));
const DownloadManager = lazyPage(() => import('./pages/admin/DownloadManager'));
const CategoryManager = lazyPage(() => import('./pages/admin/CategoryManager'));
const PackagePricing = lazyPage(() => import('./pages/admin/PackagePricing'));
const KnowledgeTagManager = lazyPage(() => import('./pages/admin/KnowledgeTagManager'));
const KnowledgePaperBuilder = lazyPage(() => import('./pages/admin/KnowledgePaperBuilder'));
const RecycleBin = lazyPage(() => import('./pages/admin/RecycleBin'));
const StudentOrders = lazyPage(() => import('./pages/admin/StudentOrders'));
const AdminTeacherOrders = lazyPage(() => import('./pages/admin/TeacherOrders'));
const PaperUpload = lazyPage(() => import('./pages/admin/PaperUpload'));
const PendingPapers = lazyPage(() => import('./pages/admin/PendingPapers'));
const Teachers = lazyPage(() => import('./pages/admin/Teachers'));
const TeacherAudit = lazyPage(() => import('./pages/admin/TeacherAudit'));
const AdminNotices = lazyPage(() => import('./pages/admin/Notices'));
const NavItems = lazyPage(() => import('./pages/admin/NavItems'));
const FooterSettings = lazyPage(() => import('./pages/admin/FooterSettings'));
const ScratchAssetsUpload = lazyPage(() => import('./pages/admin/ScratchAssetsUpload'));
const AnswerSettings = lazyPage(() => import('./pages/admin/AnswerSettings'));
const ImageSizeSettingsPage = lazyPage(() => import('./pages/admin/ImageSizeSettings'));
const PaperFontSizeSettingsPage = lazyPage(() => import('./pages/admin/PaperFontSizeSettings'));
const PointSettings = lazyPage(() => import('./pages/admin/PointSettings'));
const PointManagement = lazyPage(() => import('./pages/admin/PointManagement'));
const ErrorCorrectionReview = lazyPage(() => import('./pages/admin/ErrorCorrectionReview'));
const PaperEditor = lazyPage(() => import('./pages/admin/PaperEditor'));

// ---- 教师端 ----
const TeacherBasicInfo = lazyPage(() => import('./pages/teacher/BasicInfo'));
const TeacherStudentManagement = lazyPage(() => import('./pages/teacher/StudentManagement'));
const TeacherOrders = lazyPage(() => import('./pages/teacher/Orders'));
const TeacherMessages = lazyPage(() => import('./pages/teacher/Messages'));
const PaperReviewList = lazyPage(() => import('./pages/teacher/PaperReviewList'));
const PaperReviewDetail = lazyPage(() => import('./pages/teacher/PaperReviewDetail'));
const AssignmentPublish = lazyPage(() => import('./pages/teacher/AssignmentPublish'));
const AssignmentManage = lazyPage(() => import('./pages/teacher/AssignmentManage'));
const AssignmentGradingPage = lazyPage(() => import('./pages/teacher/AssignmentGradingPage'));
const TeacherGiftPoints = lazyPage(() => import('./pages/teacher/TeacherGiftPoints'));
const TeacherPaperEditor = lazyPage(() => import('./pages/teacher/TeacherPaperEditor'));
const TeacherPaperUpload = lazyPage(() => import('./pages/teacher/TeacherPaperUpload'));
const TeacherPapers = lazyPage(() => import('./pages/teacher/TeacherPapers'));

const LayoutWithHeader = () => (
  <>
    <Header />
    <Outlet />
    <FloatingChatButton />
  </>
);

const LayoutWithoutHeader = () => (
  <>
    <Outlet />
    <FloatingChatButton />
  </>
);

function App() {
  // 应用启动时：清除旧的localStorage缓存，确保新logo生效
  useEffect(() => {
    // 清除旧的存储桶URL缓存
    localStorage.removeItem('site_logo_url');
    localStorage.removeItem('site_logo_version');
    // 预热图片尺寸配置（后台「图片尺寸设置」可配；失败回退缓存/默认值）
    initImageSizeSettings();
    // 预热试卷字号设置（后台「试卷字号设置」可配；失败回退缓存/默认值）
    initPaperFontSizeSettings();
  }, []);

  // Pyodide 后台预加载（改善 Python 答题页面首次加载体验）
  usePyodidePreload();

  return (
    <HashRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route element={<LayoutWithHeader />}>
            <Route path="/" element={<Home />} />
            <Route path="/category/:categoryId" element={<CategoryPage />} />
            <Route path="/category/:categoryId/:subCategoryId/:levelId" element={<ExamListPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/favorites/review" element={<FavoritesReviewPage />} />
            <Route path="/wrong-questions" element={<WrongQuestionsPage />} />
            <Route path="/wrong-questions/review" element={<WrongQuestionsReviewPage />} />
            <Route path="/exam-records" element={<ExamRecordsPage />} />
            <Route path="/assignments" element={<AssignmentsPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/exam-categories" element={<ExamPlatform />} />
            <Route path="/payment-result" element={<PaymentResult />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/notices" element={<Notice />} />
            <Route path="/notice/:id" element={<NoticeDetail />} />
            <Route path="/message" element={<Message />} />
            <Route path="/downloads" element={<DownloadCenter />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/s/:code" element={<PayRedirect />} />
            <Route path="/point-center" element={<PointCenter />} />
            <Route path="/point-transactions" element={<PointTransactions />} />
            <Route path="/my-corrections" element={<MyCorrections />} />
            <Route path="/order/:orderId" element={<OrderDetail />} />
          </Route>

          <Route element={<LayoutWithoutHeader />}>
            <Route path="/python" element={<PythonPlayground />} />
            <Route path="/cpp" element={<CppPlayground />} />
            <Route path="/scratch" element={<ScratchEditorPage />} />
            <Route path="/quiz/:paperId" element={<Quiz />} />
            <Route path="/exam/:paperId" element={<Quiz />} />
            <Route path="/quiz/custom" element={<Quiz />} />
            <Route path="/exam-result/:paperId" element={<ExamResult />} />
          </Route>

          <Route path="/admin/*" element={<AdminLayout />}>
            <Route index element={<AdminPapers />} />
            <Route path="papers" element={<AdminPapers />} />
            <Route path="members" element={<AdminMembers />} />
            <Route path="admins" element={<AdminUsers />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="logs" element={<OperationLogs />} />
            <Route path="sms-settings" element={<SMSSettings />} />
            <Route path="downloads" element={<DownloadManager />} />
            <Route path="student-orders" element={<StudentOrders />} />
            <Route path="teacher-orders" element={<AdminTeacherOrders />} />
            <Route path="customer-service" element={<CustomerService />} />
            <Route path="message-config" element={<AdminMessageConfig />} />
            <Route path="message-templates" element={<MessageTemplates />} />
            <Route path="send-message" element={<SendMessage />} />
            <Route path="message-statistics" element={<MessageStatistics />} />
            <Route path="alipay-config" element={<AlipayConfig />} />
            <Route path="category-manager" element={<CategoryManager />} />
            <Route path="package-pricing" element={<PackagePricing />} />
            <Route path="knowledge-tags" element={<KnowledgeTagManager />} />
            <Route path="knowledge-paper-builder" element={<KnowledgePaperBuilder />} />
            <Route path="paper-upload" element={<PaperUpload />} />
            <Route path="pending-papers" element={<PendingPapers />} />
            <Route path="recycle-bin" element={<RecycleBin />} />
            <Route path="teachers" element={<Teachers />} />
            <Route path="teacher-audit" element={<TeacherAudit />} />
            <Route path="notices" element={<AdminNotices />} />
            <Route path="nav-items" element={<NavItems />} />
            <Route path="footer-settings" element={<FooterSettings />} />
            <Route path="scratch-assets" element={<ScratchAssetsUpload />} />
            <Route path="answer-settings" element={<AnswerSettings />} />
            <Route path="image-size-settings" element={<ImageSizeSettingsPage />} />
            <Route path="paper-font-size-settings" element={<PaperFontSizeSettingsPage />} />
            <Route path="point-settings" element={<PointSettings />} />
            <Route path="point-management" element={<PointManagement />} />
            <Route path="point-transactions" element={<PointTransactions />} />
            <Route path="error-correction-review" element={<ErrorCorrectionReview />} />
            <Route path="paper-editor" element={<PaperEditor />} />
          </Route>

          <Route path="/teacher/*" element={<TeacherLayout />}>
            <Route index element={<AssignmentManage />} />
            <Route path="basic-info" element={<TeacherBasicInfo />} />
            <Route path="students" element={<TeacherStudentManagement />} />
            <Route path="assignments" element={<AssignmentManage />} />
            <Route path="assignment-publish" element={<AssignmentPublish />} />
            <Route path="assignments/publish" element={<AssignmentPublish />} />
            <Route path="assignments/grading/:assignmentId" element={<AssignmentGradingPage />} />
            <Route path="assignments/grading/:assignmentId/:studentId" element={<AssignmentGradingPage />} />
            <Route path="paper-review" element={<PaperReviewList />} />
            <Route path="paper-review/:paperId" element={<PaperReviewDetail />} />
            <Route path="orders" element={<TeacherOrders />} />
            <Route path="messages" element={<TeacherMessages />} />
            <Route path="gift-points" element={<TeacherGiftPoints />} />
            <Route path="paper-editor" element={<TeacherPaperEditor />} />
            <Route path="paper-upload" element={<TeacherPaperUpload />} />
            <Route path="papers" element={<TeacherPapers />} />
            <Route path="exam-records" element={<ExamRecordsPage />} />
          </Route>
        </Routes>
      </Suspense>
    </HashRouter>
  );
}

export default App;
